import React, { ChangeEvent, HTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import uuid from 'uuid/v4';
import { space } from 'styled-system';

export enum VariantType {
  small = 'small',
  large = 'large',
}

type Variant = {
  containerHeight: string;
  containerWidth: string;
  transition: string;
  translateX: string;
  lineHeight: string;
};

const SMALL_VARIANT = {
  containerHeight: '16px',
  containerWidth: '30px',
  transition: '0.2s',
  translateX: '14px',
  lineHeight: '16px',
};

const LARGE_VARIANT = {
  containerHeight: '27px',
  containerWidth: '58px',
  transition: '0.4s',
  translateX: '32px',
  lineHeight: '27px',
};

const defaultVariantType = VariantType.small;
const defaultVariant = SMALL_VARIANT;
const variantMap = new Map<VariantType, Variant>([
  [VariantType.small, SMALL_VARIANT],
  [VariantType.large, LARGE_VARIANT],
]);

export type ActiveSwitchProps = HTMLAttributes<HTMLDivElement> & {
  active?: boolean;
  disabled?: boolean;
  hasLabel?: boolean;
  activeStateColor?: string;
  inactiveStateColor?: string;
  name?: string;
  onClick: (enabled: boolean) => void;
  usePropForValue?: boolean;
  variant?: VariantType;
  testId?: string;
};

export function ActiveSwitch({
  active,
  disabled,
  hasLabel,
  activeStateColor = '#7bc793',
  inactiveStateColor,
  name,
  onClick,
  usePropForValue,
  variant: variantType = defaultVariantType,
  testId = 'active-switch-input',
  ...props
}: ActiveSwitchProps): JSX.Element {
  const htmlId = uuid();
  const [value, setValue] = React.useState(active);

  function handleCheckboxChange(event: ChangeEvent<HTMLInputElement>): void {
    if (usePropForValue && typeof onClick === 'function') {
      onClick(event.target.checked);
    } else {
      const newValue = event.target.checked;
      setValue(newValue);
      onClick(newValue);
    }
  }

  const displayStatus = usePropForValue ? active : value;
  const variant = variantMap.get(variantType) ?? defaultVariant;
  const defaultInactiveStateColor = hasLabel ? '#e5e5e5' : '#e46767';
  const inactiveColor = inactiveStateColor ?? defaultInactiveStateColor;

  return (
    <Container variant={variant} {...props}>
      <Switch htmlFor={htmlId}>
        <Input
          disabled={disabled}
          checked={displayStatus}
          data-testid={testId}
          id={htmlId}
          name={name}
          onChange={handleCheckboxChange}
          type='checkbox'
        />

        <Slider
          active={displayStatus}
          variant={variant}
          activeColor={activeStateColor}
          inactiveColor={inactiveColor}
          disabled={disabled}
        >
          {variantType === 'large' && <Checkmark src='/icons/active-checkmark.svg' />}
        </Slider>
        {hasLabel && (
          <Label variant={variant} active={active}>
            {active ? 'ENABLED' : 'DISABLED'}
          </Label>
        )}
      </Switch>
    </Container>
  );
}

const Container = styled.div<{ variant: Variant }>`
  display: inline-block;
  height: ${(props): string => props.variant.containerHeight};
  width: ${(props): string => props.variant.containerWidth};
  ${space}
`;

const Switch = styled.label`
  display: inline-block;
  height: 100%;
  position: relative;
  width: 100%;
`;

const Input = styled.input`
  display: none;
`;

const Slider = styled.div<{
  active?: boolean;
  disabled?: boolean;
  activeColor: string;
  inactiveColor: string;
  variant: Variant;
}>`
  background-color: ${(props) => (props.active ? props.activeColor : props.inactiveColor)};
  border: 1px solid #98a5ac;
  border-radius: 34px;
  bottom: 0;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  left: 0;
  opacity: ${(props) => (props.disabled ? '0.7' : '1')};
  position: absolute;
  right: 0;
  top: 0;

  :before {
    background-color: #f9fdff;
    border: 1px solid #98a5ac;
    border-radius: 50%;
    content: '';
    height: ${(props) => props.variant.containerHeight};
    margin-top: -1px;
    margin-left: -1px;
    position: absolute;
    transition: ${(props) => props.variant.transition};
    width: ${(props) => props.variant.containerHeight};
    z-index: 1;
    left: 0;

    ${(props) =>
      props.active &&
      css`
        transform: translateX(${props.variant.translateX});
      `}
  }
`;

const Checkmark = styled.img`
  position: relative;
  top: 7px;
  left: 10px;
`;

const Label = styled.div<{ active?: boolean; variant: Variant }>`
  font-size: 13px;
  color: ${({ active }) => (active ? '#8cc597' : '#b3b3b3')} !important;
  font-weight: 600;
  margin-left: 40px;
  line-height: ${(props) => props.variant.lineHeight};
`;
